import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ChatHeader from "./ChatHeader";
import ChatMessages from "./ChatMessages";
import ChatQuickActions from "./ChatQuickAction";
import ChatInput from "./ChatInput";
import { chatActions } from "./chat.actions";
import { isPetasenseAdmin, isPartnerAdmin } from "common/rbac/util";
import {
  WidgetWrapper,
  WidgetContainer,
  WidgetFab,
} from "./chatbot.styled";

// Map Redux chat state (role, text, timestamp) to UI shape (id, sender, text, ...)
function mapMessagesForUI(messages) {
  return messages.map((msg) => ({
    id: msg.timestamp,
    sender: msg.role,
    text: msg.text,
    ...(msg.textKey && { textKey: msg.textKey }),
    ...(msg.textValues && { textValues: msg.textValues }),
    ...(msg.sources && { sources: msg.sources }),
    ...(Array.isArray(msg.sourceDocuments) &&
      msg.sourceDocuments.length > 0 && { sourceDocuments: msg.sourceDocuments }),
    ...(msg.imageUrl && { imageUrl: msg.imageUrl }),
  }));
}

const ROOT_TAB_PATHS = new Set(["/"]);

const normalizePath = (pathname) => {
  if (!pathname || pathname === "/") {
    return "/";
  }

  return pathname.replace(/\/+$/, "") || "/";
};

const ChatbotWidget = ({
  messages: rawMessages,
  loading,
  sendMessage,
  clearChat,
  location,
  partner,
  chatbotEnabled,
  isAllowedRole,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [hideFab, setHideFab] = useState(false);
  const fabRef = useRef(null);
  const messages = mapMessagesForUI(rawMessages);
  const pathname = normalizePath((location && location.pathname) || "/");
  const isRootTabPage = ROOT_TAB_PATHS.has(pathname);
  const showQuickActions = !rawMessages.some((m) => m.role === "user");

  const handleSend = (text) => {
    if (!text || !text.trim()) return;
    const trimmed = text.trim();
    sendMessage(trimmed);
  };

  useEffect(() => {
    if (!isRootTabPage) {
      setIsOpen(false);
      setHideFab(false);
    }
  }, [isRootTabPage]);

  useEffect(() => {
    if (!isRootTabPage) {
      return undefined;
    }

    if (isOpen) {
      setHideFab(false);
      return undefined;
    }

    const isRectVisible = (rect) => rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.top < window.innerHeight;
    const isOverlapping = (a, b) => !(a.right < b.left || a.left > b.right || a.bottom < b.top || a.top > b.bottom);

    const getFooterAnchor = () => (
      document.querySelector("footer")
      || document.getElementById("footer-status")
      || document.getElementById("footer-generate-continue")
      || document.getElementById("footer-cancel")
    );

    const updateFabVisibility = () => {
      if (!fabRef.current) {
        setHideFab(false);
        return;
      }

      const fabRect = fabRef.current.getBoundingClientRect();
      const saveButton = document.getElementById("save");
      const footerAnchor = getFooterAnchor();
      const hideForSaveButton = !!(
        saveButton
        && isRectVisible(saveButton.getBoundingClientRect())
        && isOverlapping(saveButton.getBoundingClientRect(), fabRect)
      );
      const hideForFooter = !!(
        footerAnchor
        && isRectVisible(footerAnchor.getBoundingClientRect())
      );

      setHideFab(hideForSaveButton || hideForFooter);
    };

    updateFabVisibility();

    window.addEventListener("resize", updateFabVisibility);
    window.addEventListener("scroll", updateFabVisibility, true);

    return () => {
      window.removeEventListener("resize", updateFabVisibility);
      window.removeEventListener("scroll", updateFabVisibility, true);
    };
  }, [isOpen, isRootTabPage]);

  if (!isAllowedRole || !chatbotEnabled || !isRootTabPage) {
    return null;
  }

  return (
    <WidgetWrapper>
      {isOpen ? (
        <WidgetContainer>
          <ChatHeader
            onMinimize={() => setIsOpen(false)}
            onResetContext={clearChat}
            partnerName={partner && partner.name}
          />
          <ChatMessages messages={messages} loading={loading} />
          {showQuickActions && (
            <ChatQuickActions onSelect={handleSend} disabled={loading} />
          )}
          <ChatInput onSend={handleSend} disabled={loading} />
        </WidgetContainer>
      ) : (
        !hideFab && (
        <WidgetFab
          innerRef={fabRef}
          type="button"
          onClick={() => setIsOpen(true)}
          aria-label={t('chatbot.aria.openChat')}
          title={t('chatbot.aria.openChat')}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        </WidgetFab>
        )
      )}
    </WidgetWrapper>
  );
};

const mapStateToProps = (state) => {
  const enabledFeatures = (
    state.currentAccount
    && state.currentAccount.preferences
    && state.currentAccount.preferences.enabled_features
  ) || {};
  return {
    messages: state.chat.messages,
    loading: state.chat.loading,
    partner: state.companyReducer.partner,
    chatbotEnabled: enabledFeatures.chatbot_enabled !== false,
    isAllowedRole: (
      isPetasenseAdmin(state.user && state.user.user)
      || isPartnerAdmin(state.user && state.user.user)
    ),
  };
};

const mapDispatchToProps = {
  sendMessage: chatActions.sendMessage,
  clearChat: chatActions.clearChat,
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ChatbotWidget));
